import { z } from 'zod';
import { isIsoTimestamp } from '../time';
import { timestampSchema } from './common';

// ---------------------------------------------------------------------------
// Voice calls — the end user's call routes (`…/calls`, mounted under `/api`)
//
// A call is a live voice session attached to one conversation. The caller's audio
// flows between the caller's client and the provider over WebRTC, never through the
// backend; the backend holds only the provider's server-side control connection.
//
// The browser never holds a provider credential. It sends its SDP offer, the server
// trades it with the provider (the credential and the session configuration stay
// server-side) and returns the SDP answer. `createCallResponse` is therefore an
// allowlist: parse the response through it and anything else — a provider key, the
// provider's session id or its session config — is stripped before it reaches the
// browser. `callSummary` is the same for the call itself: the end reason and the
// participant list stay server-side.
//
// Hub, conversation and user are bound when the call is created. The later requests
// name the call by `call_id` and the hub by `hub_id`, the same way other end-user
// routes name the hub they authorize against.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Vocabularies
// ---------------------------------------------------------------------------

/**
 * Where a call is in its life.
 *
 * - `connecting` — created: the SDP is traded and the media is connecting. The client
 *   plays the call's opening (the AI disclosure: the platform's, or the voice agent's own,
 *   `call-openings.ts`) with its microphone muted, then sends `ready`.
 * - `active` — `ready` arrived; the voice layer may speak.
 * - `ended` — terminal. The server records why, as one of `CALL_END_REASONS`.
 */
export const CALL_STATUSES = ['connecting', 'active', 'ended'] as const;
export const callStatus = z.enum(CALL_STATUSES);
export type CallStatus = z.infer<typeof callStatus>;

/**
 * Why a call ended. One reason per ending; the order groups them by what ended it.
 *
 * Server-side only: `callSummary` does not carry it. What a contact learns when the
 * pilot may no longer answer follows the text path's rule for the same transition, and
 * that rule discloses less than these reasons do: a reply-gate hold reaches the contact
 * exactly as a transfer to the team does.
 *
 * The caller:
 * - `caller_hangup` — the caller hung up: the hang-up request, or their client closed
 *   the media connection cleanly.
 * - `connection_lost` — the caller's media connection dropped.
 * - `ready_timeout` — the caller's client never sent `ready` in time: it failed or closed
 *   during setup, or never learned the call's id (create is sent once, never retried).
 *
 * The call's limits:
 * - `max_duration` — the call reached its maximum duration.
 * - `inactivity_timeout` — nothing was said for the inactivity timeout.
 *
 * The pilot may no longer answer the conversation (the rule the text path applies):
 * - `reply_gate_hold` — the reply gate held a call turn's draft and moved the
 *   conversation to the team.
 * - `transferred_to_team` — `transfer_to_team` ran.
 * - `team_takeover` — a team member took the conversation over.
 * - `fail_safe_escalation` — the fail-safe escalated the conversation to the team.
 * - `access_not_approved` — the contact's access became pending or blocked.
 * - `ai_mode_changed` — the hub's `ai_mode` no longer lets the pilot answer.
 * - `pilot_unavailable` — the conversation's pilot can no longer answer a call: the hub has
 *   no enabled pilot-track agent for it, or that agent's connection is a harness one.
 * - `conversation_closed` — the conversation was closed.
 *
 * Configuration (disabled or deleted):
 * - `channel_disabled` — the channel the call was placed on (the hub's app channel).
 * - `voice_agent_disabled` — the hub's `pilot_voice` agent.
 * - `connection_disabled` — the realtime connection the call runs on.
 * - `calls_disabled` — voice calls were switched off for the hub.
 *
 * Failures, which the team is told about:
 * - `provider_error` — the provider session failed or closed on its own.
 * - `call_control_lost` — call control lost the provider session and could not
 *   re-attach to it.
 */
export const CALL_END_REASONS = [
  'caller_hangup',
  'connection_lost',
  'ready_timeout',
  'max_duration',
  'inactivity_timeout',
  'reply_gate_hold',
  'transferred_to_team',
  'team_takeover',
  'fail_safe_escalation',
  'access_not_approved',
  'ai_mode_changed',
  'pilot_unavailable',
  'conversation_closed',
  'channel_disabled',
  'voice_agent_disabled',
  'connection_disabled',
  'calls_disabled',
  'provider_error',
  'call_control_lost',
] as const;
export const callEndReason = z.enum(CALL_END_REASONS);
export type CallEndReason = z.infer<typeof callEndReason>;

/**
 * How the caller's audio reaches the voice layer.
 *
 * - `webrtc` — the caller's client connects straight to the provider (the app channel).
 *
 * Reserved for later phases; nothing writes them yet:
 * - `sip` — SIP ingress (phone, WhatsApp).
 * - `sfu` — an SFU room that team members can join.
 */
export const CALL_TRANSPORTS = ['webrtc', 'sip', 'sfu'] as const;
export const callTransport = z.enum(CALL_TRANSPORTS);
export type CallTransport = z.infer<typeof callTransport>;

/**
 * Who is on a call. Server-side only, like the end reason: `callSummary` does not carry
 * the participant list.
 *
 * - `user` — the caller.
 * - `voice_agent` — the voice layer, configured by the hub's `pilot_voice` agent.
 *
 * Reserved for human calls; nothing writes them yet:
 * - `team_member` — a team member on the call.
 * - `supervisor` — a team member listening in.
 */
export const CALL_PARTICIPANT_TYPES = ['user', 'voice_agent', 'team_member', 'supervisor'] as const;
export const callParticipantType = z.enum(CALL_PARTICIPANT_TYPES);
export type CallParticipantType = z.infer<typeof callParticipantType>;

// ---------------------------------------------------------------------------
// Shared fields
// ---------------------------------------------------------------------------

/** An instant in the canonical form only (AGENTS.md › Timestamps). */
const callInstant = timestampSchema.refine(isIsoTimestamp, 'Expected a canonical ISO timestamp');

/**
 * Cap on the browser's SDP offer, in characters. The route forwards the offer to the
 * provider, and `/api/*` carries no blanket body limit.
 *
 * Measured: a Chromium 152 offer with one audio transceiver and one data channel is
 * 1,701 characters, and 2,765 once its 8 ICE candidates are gathered. 32 KiB leaves
 * more than ten times that for extra network interfaces and codecs.
 */
export const MAX_SDP_OFFER_LENGTH = 32 * 1024;

/** Every SDP description opens with its version line, `v=0`. */
const sdpDescription = z.string().regex(/^v=0\r?\n/, 'Expected an SDP description (starting with "v=0")');

const sdpOffer = sdpDescription.max(MAX_SDP_OFFER_LENGTH);

/**
 * Cap on the body of any call request, in bytes: the route reads a body before Zod bounds
 * it. Sized for create, whose SDP offer JSON-escapes each line break (`\r\n`) to four
 * characters: twice the offer's own cap leaves room for that and the other fields.
 */
export const MAX_CALL_REQUEST_BODY_BYTES = 2 * MAX_SDP_OFFER_LENGTH;

const hubScoped = { hub_id: z.string().uuid() };

// ---------------------------------------------------------------------------
// The call as the caller's client sees it
// ---------------------------------------------------------------------------

const callSummaryFields = {
  call_id: z.string().uuid(),
  conversation_id: z.string().uuid(),
  transport: callTransport,
  /** When the call was created. */
  started_at: callInstant,
};

/**
 * One call, as its caller may see it. `ended_at` is set exactly when `status` is
 * `ended`, so a client narrowing on `status` reads it without a null check.
 */
export const callSummary = z.discriminatedUnion('status', [
  z.object({
    ...callSummaryFields,
    status: callStatus.exclude(['ended']),
    ended_at: z.null(),
  }),
  z.object({
    ...callSummaryFields,
    status: z.literal('ended'),
    ended_at: callInstant,
  }),
]);
export type CallSummary = z.infer<typeof callSummary>;

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

export const callIdParam = z.object({
  call_id: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// POST …/calls — create a call
// ---------------------------------------------------------------------------

export const createCallBody = z.object({
  ...hubScoped,
  /**
   * Absent: the call joins the caller's active conversation on the hub, which the call
   * starts when there is none. Present: the call joins that conversation, which must be
   * the caller's own.
   *
   * UUID-validated because conversation allocation treats a non-UUID id as absent: a
   * malformed id would skip the owner check and mint a new conversation instead of
   * being refused. An explicit `null` means absent.
   */
  conversation_id: z.string().uuid().nullish().transform((v) => v ?? undefined),
  /** The browser's SDP offer, sent to the provider unchanged. */
  sdp_offer: sdpOffer,
  /**
   * The caller ACKNOWLEDGED THE RECORDING WARNING: before this create, their client showed
   * that the call will be recorded, and the caller chose to go on. A hub that records calls
   * records only a call whose create says so: a client that did not show the warning (one
   * that predates it, or one whose hub signal said the hub does not record) gets an
   * unrecorded call, never a recorded call its caller did not acknowledge. Absent: false.
   *
   * It replaces `plays_recording_notice` (the audio notice's flag), which no server reads any
   * more: a client that sends only that one gets an unrecorded call.
   */
  recording_warning_acknowledged: z.boolean().optional(),
});
export type CreateCallBody = z.infer<typeof createCallBody>;

export const createCallResponse = z.object({
  call_id: z.string().uuid(),
  /** The conversation the call is attached to: the one named, or else the caller's active one. */
  conversation_id: z.string().uuid(),
  /** The provider's SDP answer, for the browser's `setRemoteDescription`. */
  sdp_answer: sdpDescription,
  /**
   * The call is recorded: its hub records calls and the create said the caller acknowledged
   * the recording warning. The client shows that the call is recorded for as long as it
   * lasts, and the recording starts only once `ready` arrives. Absent only from a server that
   * predates recording, which records nothing: absent means not recorded.
   */
  recorded: z.boolean().optional(),
});
export type CreateCallResponse = z.infer<typeof createCallResponse>;

/**
 * Why a create was refused (a 409's `details.reason`). Never which check refused it:
 * - `calls_unavailable` — the hub cannot take calls now: the rollout gate, the hub type, its
 *   voice agent, its connection or that connection's key, or a pilot that cannot answer a
 *   call (none, or one on a harness);
 * - `ai_unavailable` — the AI may not answer this conversation now: a person holds it, the
 *   hub's mode has no pilot, or the contact is not approved;
 * - `quota_exceeded` — the organization's free-plan operations are used up;
 * - `call_in_progress` — the conversation already has a live call (one per conversation).
 */
export const CALL_REFUSAL_REASONS = ['calls_unavailable', 'ai_unavailable', 'quota_exceeded', 'call_in_progress'] as const;
export type CallRefusalReason = (typeof CALL_REFUSAL_REASONS)[number];

// ---------------------------------------------------------------------------
// POST …/calls/:call_id/ready — the disclosure has played
// POST …/calls/:call_id/hangup — the caller hangs up
// GET  …/calls/:call_id — the call's status
// ---------------------------------------------------------------------------

export const callReadyBody = z.object(hubScoped);
export type CallReadyBody = z.infer<typeof callReadyBody>;

export const callHangupBody = z.object(hubScoped);
export type CallHangupBody = z.infer<typeof callHangupBody>;

export const callStatusQuery = z.object(hubScoped);
export type CallStatusQuery = z.infer<typeof callStatusQuery>;

// ---------------------------------------------------------------------------
// POST …/calls/:call_id/delegations — the delegations the caller's client saw
//
// Gap recovery. The provider replays nothing to a control connection that attaches
// late, so a delegation the voice layer raises while the server holds no control
// connection (a backend deploy, a restart) reaches only the caller's client. The client
// reports the ids it saw; the server answers each id it never saw with a fixed request
// to repeat the question, and ignores the ones it already answered. A forged or stale
// id buys nothing: the server fixes what is said, and the provider refuses an id it
// does not know.
// ---------------------------------------------------------------------------

/** Most delegation ids one report carries. The client reports a few seconds' worth. */
export const MAX_REPORTED_DELEGATION_IDS = 16;

/**
 * A provider delegation id, opaque to WayAI (GPT-Live's look like `item_…`). The client
 * reads ids off the provider's events and reports only the ones this accepts.
 */
export const callDelegationId = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/, 'Expected a provider delegation id');

export const callDelegationsBody = z.object({
  ...hubScoped,
  delegation_ids: z.array(callDelegationId).min(1).max(MAX_REPORTED_DELEGATION_IDS),
});
export type CallDelegationsBody = z.infer<typeof callDelegationsBody>;

/**
 * The response to `ready`, hang-up, the delegation report and the status read: the call
 * after the request.
 */
export const callResponse = z.object({
  call: callSummary,
});
export type CallResponse = z.infer<typeof callResponse>;

// ---------------------------------------------------------------------------
// The call's recording — support's, never the caller's
//
// A hub whose voice agent records calls (`record_calls`) keeps each recorded call's audio,
// both sides, as one stereo WebM Opus file on the conversation (`audio/webm`; a recording
// finished before WebM Opus is a WAV, `audio/wav`), the caller on the left channel and the
// voice agent on the right. The file rides a system note to the
// team (`sender_type: 'system'`, `receiver_type: 'team'`, kept out of every history), so it
// reaches the team only — the support inbox, and for an eval call, whose conversation no inbox
// lists, the eval call's record (`evalCallRecording`, played on the eval call's page): the
// caller's views, the conversation's channels and the AI's file tools never carry it. The
// note's `metadata.call` is `CallRecordingNoteMetadata`.
// ---------------------------------------------------------------------------

/** Most gaps a recording's note lists; the gaps past the cap are merged into its last one. */
export const MAX_CALL_RECORDING_GAPS = 500;

/**
 * A stretch of the recording with no call audio in it — silence stands in its place: the
 * gap a deploy leaves while call control re-attaches, the audio a restart lost before it was
 * stored, or a part that could not be stored. On the recording's own timeline, in
 * milliseconds from its start.
 */
export const callRecordingGap = z.object({
  start_ms: z.number().int().nonnegative(),
  end_ms: z.number().int().positive(),
}).refine((gap) => gap.end_ms > gap.start_ms, 'A gap ends after it starts');
export type CallRecordingGap = z.infer<typeof callRecordingGap>;

export const callRecordingNoteMetadata = z.object({
  call_id: z.string().min(1),
  team_notice: z.literal('call_recording'),
  recording: z.object({
    /** The recording's `conversation_file` row, the note's one file. */
    file_id: z.string().uuid(),
    /** When its first audio arrived: its time 0. */
    started_at: callInstant,
    duration_ms: z.number().int().nonnegative(),
    /** Ascending and disjoint, each within the recording. */
    gaps: z.array(callRecordingGap).max(MAX_CALL_RECORDING_GAPS),
  }),
});
export type CallRecordingNoteMetadata = z.infer<typeof callRecordingNoteMetadata>;
